import { Space, Statistic, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import duration, { type Duration } from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useInterval } from 'usehooks-ts';

const { Countdown } = Statistic;
const { Text } = Typography;

export const CountdownHumanizedWrapper = styled.div`
  > .ant-typography {
    padding-bottom: 4px;
    display: inline-block;
  }

  > .ant-tag {
    width: 100%;
  }

  > .countdown-tag {
    color: #a0a0a0;
    background: #f5f5f5;
    border-color: #d6d6d6;
  }

  .ant-statistic {
    color: inherit;

    > .ant-statistic-content {
      color: inherit;
    }
  }

  .ant-statistic-content {
    text-align: center;
  }
`;

export const CountdownLabelStrip = styled.div`
  display: flex;
  justify-content: center;
  margin-top: -10px;

  .label {
    width: 25px;
    text-align: center;
    font-size: 10px;
    padding-bottom: 4px;
  }
`;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

export interface CountdownHumanizedProps {
  expireDate: null | undefined | string;
}

const ALERT_MINUTES = 60 * 12; // visualizzazione countdown red
const WARN_HOURS = 48; // visualizzazione contudown arancio
const START_COUNTDOWN_DAYS = 60; // Visualizzazione countdown blu
//ATTENZIONE Visualizzazione testo: 'Più di due mesi'

/**
 * Mostra il countdown alla scadenza della presentazione di una domanda.
 */
export function CountdownHumanized(props: CountdownHumanizedProps) {
  const [dateNow, setDateNow] = useState(dayjs().tz('Europe/Rome'));
  useInterval(() => {
    setDateNow(dayjs().tz('Europe/Rome'));
  }, 1000);
  const { t } = useTranslation();

  if (!props.expireDate) return null;

  // definiamo il periodo di durata
  const date = dayjs(props.expireDate).tz('Europe/Rome');
  const duration = dayjs.duration(date.diff(dateNow));

  // se la durata non è valida non mostriamo l'elemento
  if (duration.asMilliseconds() === 0) return null;

  // se la durata è minore di un secondo non mostriamo l'elemento
  if (duration.asSeconds() < 0) return null;
  // if (date.diff(dateNow, 'seconds') < 0) return null;

  const diffDays = duration.asDays();

  const durationText = (duration: Duration): string => {
    if (duration.asYears() >= 1)
      return t('countdown-humanized.more-years', 'Più di un anno');
    if (diffDays > START_COUNTDOWN_DAYS)
      return t('countdown-humanized.more-months', 'Più di due mesi');
    return `${duration.days()} ${t('countdown-humanized.days', 'giorni')}`;
  };

  const tagColor =
    duration.asHours() > WARN_HOURS
      ? 'blue'
      : duration.asMinutes() > ALERT_MINUTES
        ? 'warning'
        : 'error';

  return (
    <CountdownHumanizedWrapper>
      <Text type="secondary">
        {t('countdown-humanized.label', 'Tempo rimanente')}:{' '}
      </Text>

      {diffDays > START_COUNTDOWN_DAYS ? (
        <Tag className="countdown-tag">
          <Statistic value={durationText(duration)} />
        </Tag>
      ) : (
        <Tag color={tagColor}>
          <Countdown value={date.format()} format="DD : HH : mm : ss" />
          <CountdownLabelStrip>
            <Space size={18}>
              <div className="label">gg/dd</div>
              <div className="label">hh</div>
              <div className="label">mm</div>
              <div className="label">ss</div>
            </Space>
          </CountdownLabelStrip>
        </Tag>
      )}
    </CountdownHumanizedWrapper>
  );
}
